export const REMINDER_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type ReminderOccurrenceShape = {
  title?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  repeatEndDate?: string | null;
  repeatOccurrences?: number | string | null;
  reminderTime?: string | null;
  endTime?: string | null;
  eventEndDate?: string | null;
  daysOfWeek?: string[] | null;
  repeatType?: string | null;
};

function datePartsInTimeZone(date: Date, timeZone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour),
      minute: Number(values.minute),
      second: Number(values.second),
    };
  } catch {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
  }
}

export function getDateKeyInTimeZone(date: Date, timeZone = 'America/Chicago'): string {
  const parts = datePartsInTimeZone(date, timeZone);
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

export function addCalendarDays(dateKey: string, amount: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + amount, 12, 0, 0));
  return date.toISOString().slice(0, 10);
}

export function getDateKeys(startDateKey: string, count: number): string[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => addCalendarDays(startDateKey, index));
}

export function getCurrentWeekDateKeys(dateKey: string): string[] {
  const [year, month, day] = dateKey.split('-').map(Number);
  const dayOfWeek = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).getUTCDay();
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(dateKey, index - dayOfWeek));
}

export function reminderOccursOnDate(
  reminder: ReminderOccurrenceShape,
  dateKey: string,
): boolean {
  const daysOfWeek = Array.isArray(reminder.daysOfWeek) ? reminder.daysOfWeek : [];
  const repeatType = reminder.repeatType || (daysOfWeek.length > 0 ? 'weekly' : 'never');
  const isOnce = repeatType === 'once' || repeatType === 'never';

  if (isOnce) {
    return (reminder.startDate || '') === dateKey;
  }

  if (reminder.startDate && dateKey < reminder.startDate) return false;
  const recurrenceEndDate = reminder.repeatEndDate || reminder.endDate;
  if (recurrenceEndDate && dateKey > recurrenceEndDate) return false;

  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  if (repeatType === 'daily') return true;

  if (repeatType === 'monthly') {
    const startDay = reminder.startDate
      ? Number(reminder.startDate.slice(8, 10))
      : date.getUTCDate();
    return date.getUTCDate() === startDay;
  }

  if (repeatType === 'yearly') {
    if (!reminder.startDate) return false;
    return date.getUTCMonth() === Number(reminder.startDate.slice(5, 7)) - 1
      && date.getUTCDate() === Number(reminder.startDate.slice(8, 10));
  }

  const weekday = REMINDER_WEEKDAYS[date.getUTCDay()];
  if (daysOfWeek.length === 0 && reminder.startDate) {
    const startDate = new Date(`${reminder.startDate}T12:00:00Z`);
    return date.getUTCDay() === startDate.getUTCDay();
  }
  return daysOfWeek.some((day) => {
    const normalizedDay = String(day).trim().slice(0, 3);
    return normalizedDay === weekday;
  });
}

/**
 * Convert a local date/time in an IANA timezone into an instant.
 * This avoids treating a hub-local reminder time as the browser's local time.
 */
export function reminderOccurrenceStart(
  reminder: ReminderOccurrenceShape,
  dateKey: string,
  timeZone = 'America/Chicago',
): Date {
  return reminderOccurrenceDateTime(reminder.reminderTime, dateKey, timeZone);
}

function reminderOccurrenceDateTime(
  time: string | null | undefined,
  dateKey: string,
  timeZone: string,
): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  const [hour = '0', minute = '0'] = (time || '00:00').split(':');
  const localAsUtc = Date.UTC(year, month - 1, day, Number(hour), Number(minute), 0, 0);
  let guess = new Date(localAsUtc);

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = datePartsInTimeZone(guess, timeZone);
    const renderedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      0,
    );
    guess = new Date(localAsUtc - (renderedAsUtc - guess.getTime()));
  }

  return guess;
}

export function reminderTimeToMinutes(time: string | null | undefined): number {
  if (!time) return 0;
  const [hour = '0', minute = '0'] = time.split(':');
  return Number(hour) * 60 + Number(minute);
}

export type ReminderOccurrence = {
  dateKey: string;
  start: Date;
};

/**
 * Find the next scheduled occurrence at or after a reference instant.
 *
 * The scan is intentionally date-based: reminderOccursOnDate remains the
 * single recurrence authority, while reminderOccurrenceStart keeps the
 * timezone/DST conversion for the matching occurrence.
 */
export function getNextReminderOccurrence(
  reminder: ReminderOccurrenceShape,
  reference = new Date(),
  timeZone = 'America/Chicago',
  maxDays = 366 * 2,
): ReminderOccurrence | null {
  const isAllDay = !reminder.reminderTime;

  const referenceDateKey = getDateKeyInTimeZone(reference, timeZone);
  for (let offset = 0; offset <= maxDays; offset += 1) {
    const dateKey = addCalendarDays(referenceDateKey, offset);
    if (reminderOccursOnDate(reminder, dateKey)) {
      const start = reminderOccurrenceStart(reminder, dateKey, timeZone);
      // All-day events belong to their local calendar date even though their
      // normalized start instant is midnight and may already have passed.
      if (isAllDay || start.getTime() >= reference.getTime()) {
        return { dateKey, start };
      }
    }
  }

  return null;
}

/**
 * Return the user-facing countdown for the next actual occurrence.
 * Passed one-time occurrences return null instead of a misleading future
 * value; recurring reminders naturally advance to their next occurrence.
 */
export function getReminderCountdown(
  reminder: ReminderOccurrenceShape,
  reference = new Date(),
  timeZone = 'America/Chicago',
): { dateKey: string; daysUntil: number; label: string } | null {
  const next = getNextReminderOccurrence(reminder, reference, timeZone);
  if (!next) return null;

  const currentDateKey = getDateKeyInTimeZone(reference, timeZone);
  const [currentYear, currentMonth, currentDay] = currentDateKey.split('-').map(Number);
  const [nextYear, nextMonth, nextDay] = next.dateKey.split('-').map(Number);
  const currentNoon = Date.UTC(currentYear, currentMonth - 1, currentDay, 12);
  const nextNoon = Date.UTC(nextYear, nextMonth - 1, nextDay, 12);
  const daysUntil = Math.round((nextNoon - currentNoon) / 86_400_000);

  return {
    dateKey: next.dateKey,
    daysUntil,
    label: daysUntil === 0
      ? 'Today'
      : `${daysUntil} day${daysUntil === 1 ? '' : 's'} until${reminder.title ? ` ${reminder.title}` : ''}`,
  };
}

/**
 * Resolve the end of a reminder occurrence in the same timezone as its start.
 *
 * A recurring reminder's end time is attached to each occurrence date. When
 * no explicit event end date is supplied, an end clock time earlier than the
 * start clock time means the event crosses midnight. Equal times remain a
 * zero-duration same-day range.
 */
export function reminderOccurrenceEnd(
  reminder: ReminderOccurrenceShape,
  dateKey: string,
  timeZone = 'America/Chicago',
): Date | null {
  if (!reminder.endTime) return null;

  const isRecurring = reminder.repeatType && reminder.repeatType !== 'never' && reminder.repeatType !== 'once';
  const explicitEndDate = !isRecurring && reminder.eventEndDate;
  let endDateKey = explicitEndDate || dateKey;

  if (!explicitEndDate) {
    const startMinutes = reminderTimeToMinutes(reminder.reminderTime);
    const endMinutes = reminderTimeToMinutes(reminder.endTime);
    if (endMinutes >= 0 && startMinutes >= 0 && endMinutes < startMinutes) {
      endDateKey = addCalendarDays(dateKey, 1);
    }
  }

  return reminderOccurrenceDateTime(reminder.endTime, endDateKey, timeZone);
}

export function validateReminderTimeRange(reminder: ReminderOccurrenceShape): string | null {
  const timePattern = /^(?:[01]?\d|2[0-3]):[0-5]\d$/;
  if (reminder.reminderTime && !timePattern.test(reminder.reminderTime)) {
    return 'reminderTime must use HH:MM format';
  }
  if (reminder.endTime && !timePattern.test(reminder.endTime)) {
    return 'endTime must use HH:MM format';
  }
  if (!reminder.endTime) {
    if (reminder.eventEndDate) return 'eventEndDate requires endTime';
    return null;
  }

  const endDate = reminder.eventEndDate;
  const startDate = reminder.startDate;
  if (endDate && startDate) {
    if (endDate < startDate) return 'eventEndDate cannot be before startDate';
    if (
      endDate === startDate &&
      reminderTimeToMinutes(reminder.endTime) < reminderTimeToMinutes(reminder.reminderTime)
    ) {
      return 'endTime cannot be before start time when eventEndDate is the same date';
    }
  }

  return null;
}